<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { ISettingsBookkeepingItem } from '@modules/general-settings/submodules/bookkeeping/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import { useFormatDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { GENERAL_SETTINGS_INNER_CARD_CLASSES, TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_BOOKKEEPING_PERMISSIONS,
  ADMIN_GENERAL_SETTINGS_BOOKKEEPING_UPDATE_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING,
} from '@modules/general-settings/submodules/bookkeeping/constants';
import {
  useSettingsBookkeepingCreateMutation,
  useSettingsBookkeepingDeleteMutation,
} from '@modules/general-settings/submodules/bookkeeping/mutations';
import { useSettingsBookkeepingListQuery } from '@modules/general-settings/submodules/bookkeeping/queries';
import { useI18n } from '@modules/i18n/composables';
import { useMerchantIdStore } from '@modules/merchant/store';

const { getTranslation } = useI18n();
const { formatDate } = useFormatDate();
const { merchantId } = useMerchantIdStore();
const router = useRouter();

const { data, isLoading } = useSettingsBookkeepingListQuery();
const {
  mutateAsync: createItem,
  errors,
  isLoading: isLoadingSubmission,
  clearError,
} = useSettingsBookkeepingCreateMutation();
const { mutateAsync: deleteItem } = useSettingsBookkeepingDeleteMutation();

const formSchema = computed<FormSchema<ISettingsBookkeepingItem>>(() => ({
  limit: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'FORM_LIMIT').value,
    required: true,
  },
  date: {
    type: EFormInputTypes.DATE,
    view: 'month',
    dateFormat: 'mm.yy',
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_BOOKKEEPING, 'FORM_DATE').value,
    required: true,
  },
}));

function handleSubmit(values: ISettingsBookkeepingItem): void {
  void createItem({
    history_of_limit_change: {
      limit: values.limit,
      date: formatDate(values.date, 'MM.yyyy'),
    },
  });
}

function handleEdit(item: IChangeHistoryEntry): void {
  void router.push({ name: ADMIN_GENERAL_SETTINGS_BOOKKEEPING_UPDATE_ROUTE_NAME, params: { merchantId, itemId: item.id } });
}

function handleDelete(item: IChangeHistoryEntry): void {
  void deleteItem({ id: item.id });
}
</script>

<template>
  <div v-data-qa-auto-id="'AdminGeneralSettingsBookkeepingPage'">
    <VCard
      type="form"
      :class="GENERAL_SETTINGS_INNER_CARD_CLASSES"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
        :custom-errors="errors"
        :is-loading="isLoadingSubmission"
        @interacted="clearError"
        @submit="handleSubmit"
      />
    </VCard>
    <VLoader :loading="isLoading">
      <VChangeHistory
        :list="data?.list"
        :edit-permission="ADMIN_GENERAL_SETTINGS_BOOKKEEPING_PERMISSIONS.HISTORYOFLIMITCHANGES_EDIT"
        :delete-permission="ADMIN_GENERAL_SETTINGS_BOOKKEEPING_PERMISSIONS.HISTORYOFLIMITCHANGES_DELETE"
        @edit="handleEdit"
        @delete="handleDelete"
      />
    </VLoader>
  </div>
</template>
